import type { UtrAxis, UtrLeaderboardToken } from '@forgd/contract/types'
import type { UtrGradingScaleRow } from '@forgd/shared/universal-token-rating'

export const UTR_SCORE_TOOLTIP = 'Scored 0–10 against the Universal Token Rating scoring rubric — higher is better. The letter grade maps directly to the score, and the percentile shows the share of all rated tokens this token outperforms.'

export { utrHeatmapColor } from '@forgd/shared/universal-token-rating'

export type UtrScoreMetric = 'grade' | 'score' | 'percentile'

export interface RatingCategoryScore {
  label: string
  grade: string | null
  score: number | null
  percentile: number | null
}

export interface RatingMatrixPoint {
  id: string
  name: string
  image?: string | null
  x: number
  y: number
}

export function utrAxisCategories(token: UtrLeaderboardToken, axis: UtrAxis): RatingCategoryScore[] {
  return token.categories
    .filter(category => category.axis === axis)
    .map(category => ({
      label: category.category,
      grade: category.grade,
      score: category.percentageScore * 10,
      percentile: category.percentile,
    }))
}

const UTR_GRADE_SORT_INDEX: Record<string, number> = {
  'AAA': 0,
  'AA': 1,
  'A': 2,
  'BBB': 3,
  'BB': 4,
  'B': 5,
  'CCC': 6,
  'N/A': 7,
}

/** Position in the highest-to-lowest UTR grade sort order. */
export function utrGradeSortIndex(grade: string): number {
  return UTR_GRADE_SORT_INDEX[grade] ?? 7
}

const UTR_GRADE_CHIP_CLASS: Record<string, string> = {
  'AAA': 'bg-heatmap-strong-100',
  'AA': 'bg-heatmap-strong-90',
  'A': 'bg-heatmap-strong-80',
  'BBB': 'bg-heatmap-strong-60',
  'BB': 'bg-heatmap-strong-40',
  'B': 'bg-heatmap-strong-20',
  'CCC': 'bg-heatmap-strong-0',
  'N/A': 'bg-neutral-400',
}

function utrGradeClass(map: Record<string, string>, grade: string): string {
  // The trailing '' never hits — it only satisfies noUncheckedIndexedAccess.
  return map[grade] ?? map['N/A'] ?? ''
}

export function utrLetterGradeChipClass(grade: string): string {
  return utrGradeClass(UTR_GRADE_CHIP_CLASS, grade)
}

// Score-bar fills per the design's valueSlot variants: identical to the chip
// stops except B, which the design binds to /10 instead of /20.
const UTR_GRADE_BAR_CLASS: Record<string, string> = {
  'AAA': 'bg-heatmap-strong-100',
  'AA': 'bg-heatmap-strong-90',
  'A': 'bg-heatmap-strong-80',
  'BBB': 'bg-heatmap-strong-60',
  'BB': 'bg-heatmap-strong-40',
  'B': 'bg-heatmap-strong-10',
  'CCC': 'bg-heatmap-strong-0',
  'N/A': 'bg-neutral-400',
}

export function utrLetterGradeBarClass(grade: string): string {
  return utrGradeClass(UTR_GRADE_BAR_CLASS, grade)
}

const UTR_GRADE_TEXT_CLASS: Record<string, string> = {
  'AAA': 'text-heatmap-strong-100',
  'AA': 'text-heatmap-strong-90',
  'A': 'text-heatmap-strong-80',
  'BBB': 'text-heatmap-strong-60',
  'BB': 'text-heatmap-strong-40',
  'B': 'text-heatmap-strong-20',
  'CCC': 'text-heatmap-strong-0',
  'N/A': 'text-disabled/30',
}

export function utrLetterGradeTextClass(grade: string): string {
  return utrGradeClass(UTR_GRADE_TEXT_CLASS, grade)
}

const UTR_GRADE_STAT_CLASS: Record<string, string> = {
  'AAA': 'bg-green-600/10 text-heatmap-strong-100',
  'AA': 'bg-green-600/10 text-heatmap-strong-90',
  'A': 'bg-green-600/10 text-heatmap-strong-80',
  'BBB': 'bg-yellow-700/10 text-heatmap-strong-60',
  'BB': 'bg-yellow-700/10 text-heatmap-strong-40',
  'B': 'bg-yellow-700/10 text-heatmap-strong-20',
  'CCC': 'bg-red-600/10 text-heatmap-strong-0',
  'N/A': 'bg-neutral-600/30 text-disabled/30',
}

export function utrLetterGradeStatClass(grade: string): string {
  return utrGradeClass(UTR_GRADE_STAT_CLASS, grade)
}

/**
 * Letter grade for a score on a config grading scale. The score must be in the
 * scale's own unit (0–10 for the disclosure and performance axes, 0–100 for
 * overall). A null `min` row is the catch-all floor.
 */
export function utrGradeFromScale(score: number, scale: UtrGradingScaleRow[] | undefined): string {
  if (!scale) {
    return 'N/A'
  }
  let match: UtrGradingScaleRow | null = null
  for (const row of scale) {
    if (row.min !== null && score < row.min) {
      continue
    }
    if (!match || (row.min ?? Number.NEGATIVE_INFINITY) > (match.min ?? Number.NEGATIVE_INFINITY)) {
      match = row
    }
  }
  return match?.grade ?? 'N/A'
}
